import React, { useState } from 'react';
import 'keen-slider/keen-slider.min.css';
import { useKeenSlider } from 'keen-slider/react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';

// --- DATA: Features Slider ---
const features = [
    {
        id: 1,
        title: "Vetted, Experienced Developers",
        icon: "/assets/icons/triangle.svg", // Placeholder for the graphic icon
    },
    {
        id: 2,
        title: "Dedicated In-House Team, No Outsourcing",
        icon: "/assets/icons/triangle.svg",
    },
    {
        id: 3,
        title: "Scalable Teams on Demand",
        icon: "/assets/icons/triangle.svg",
    },
    {
        id: 4,
        title: "Strict NDA & IP Protection",
        icon: "/assets/icons/triangle.svg",
    },
];

// --- DATA: Stats Grid ---
const stats = [
    {
        id: 1,
        value: "8+",
        label: "Years In Business",
        borderColor: "blue", // Blue Line
    },
    {
        id: 2,
        value: "15+",
        label: "Industries Serve",
        borderColor: "orange", // Orange Line
    },
    {
        id: 3,
        value: "50+",
        label: "Projects Delivered",
        borderColor: "blue", // Blue Line
    },
    {
        id: 4,
        value: "95%",
        label: "Client Retention",
        borderColor: "blue", // Blue Line
    },
];

const WhyChooseSection = () => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [loaded, setLoaded] = useState(false);

    // --- SLIDER CONFIG ---
    const [sliderRef, instanceRef] = useKeenSlider({
        initial: 0,
        slideChanged(slider) {
            setCurrentSlide(slider.track.details.rel);
        },
        created() {
            setLoaded(true);
        },
        slides: {
            perView: 1.2,
            spacing: 20,
        },
        breakpoints: {
            '(min-width: 768px)': {
                slides: { perView: 2.2, spacing: 30 },
            },
            '(min-width: 1024px)': {
                slides: { perView: 2.5, spacing: 40 },
            },
        },
    });

    return (
        <section className="w-full py-20 bg-white font-sans overflow-hidden">
            <div className="max-w-[1440px] mx-auto px-6 md:px-[80px]">
                <div className="flex flex-col md:flex-row justify-between items-end mb-12 gap-6">
                    <motion.h2
                        initial={{ opacity: 0, x: -50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="text-[#01386A] text-[20px] md:text-[30px] font-[600] max-w-[604px] font-sora leading-tight"
                    >
                        Why Choose Mobikode for Contract Hiring of Software Developers?
                    </motion.h2>

                    {/* Navigation Arrows */}
                    <div className="flex gap-4">
                        <button
                            onClick={(e) => e.stopPropagation() || instanceRef.current?.prev()}
                            className="w-10 h-10 rounded-full border border-[#F06115] text-[#F06115] flex items-center justify-center hover:bg-[#F06115] hover:text-white transition-all duration-300"
                        >
                            <ArrowLeft size={18} />
                        </button>
                        <button
                            onClick={(e) => e.stopPropagation() || instanceRef.current?.next()}
                            className="w-10 h-10 rounded-full border border-[#F06115] text-[#F06115] flex items-center justify-center hover:bg-[#F06115] hover:text-white transition-all duration-300"
                        >
                            <ArrowRight size={18} />
                        </button>
                    </div>
                </div>

                <div className="mb-24">
                    <div ref={sliderRef} className="keen-slider">
                        {features.map((feature) => (
                            <div key={feature.id} className="keen-slider__slide">
                                <div className="bg-[#F0F0F0] p-8 h-[140px] flex items-center gap-[30px] hover:shadow-lg transition-shadow duration-300">
                                    <div>
                                        <img src="/assets/hire-n/wrap2.svg" alt="" />
                                    </div>
                                    <h3 className="text-[#0E2A62] text-[14px] lg:text-[16px] font-[500] font-graphik leading-tight">
                                        {feature.title}
                                    </h3>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="flex flex-col lg:flex-row items-center relative z-0">

                    {/* LEFT: IMAGE SECTION */}
                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                        className="w-full lg:w-full max-w-[716px] h-[400px] lg:h-[496px] relative overflow-hidden z-0"
                    >
                        <img
                            src="/assets/hire-n/img2.png"
                            alt="Technology Background"
                            className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-[#0E2A62]/20 mix-blend-multiply"></div>
                    </motion.div>

                    {/* RIGHT: STATS GRID */}
                    <div className="w-full lg:w-full max-w-[820px] lg:-ml-[250px] relative z-10 bg-transparent flex flex-col justify-center">

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-[20px]">
                            {stats.map((stat, index) => (
                                <motion.div
                                    key={stat.id}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.5, delay: index * 0.1 }}
                                    className="flex flex-col gap-[20px] lg:py-[40px] py-[20px] lg:px-[40px] px-[20px] bg-[#FFFFFF] relative pb-8 group cursor-pointer hover:shadow-xl transition-shadow duration-300"
                                >
                                    <h3 className="text-[#01386A] text-[32px] md:text-[40px] font-[600] leading-none font-sora">
                                        {stat.value}
                                    </h3>
                                    <p className="text-[#49577A] text-[14px] md:text-[16px] font-[500] font-graphik">
                                        {stat.label}
                                    </p>
                                    <div className="absolute bottom-0 left-0 w-full h-[3px] bg-[#0E2A62]"></div>
                                    <div className="absolute bottom-0 left-0 h-[3px] bg-[#F06115] w-0 group-hover:w-full transition-all duration-700 ease-in-out z-10"></div>

                                </motion.div>
                            ))}
                        </div>

                    </div>

                </div>
            </div>
        </section>
    );
};

export default WhyChooseSection;